import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FiLink, FiCopy, FiTrash2, FiX } from 'react-icons/fi';
import { showNotification } from './Notification';
import ConfirmDialog from './ConfirmDialog';
import ProgressBar from './ProgressBar';
import usePublicLinksBaseUrl from '../hooks/usePublicLinksBaseUrl';
import './ProfileLinks.css';

const fieldLabels = {
  serial_number: 'Serial Number (SN)',
  tag_number: 'Tag Number',
  first_name: 'First Name',
  last_name: 'Last Name',
  sex: 'Sex',
  date_of_birth: 'Date of Birth (DOB)',
  pwd: 'PWD',
  phone_number: 'Phone Number',
  national_id_number: 'National ID Number (NIN)',
  bank_verification_number: 'Bank Verification Number (BVN)',
  account_number: 'Account Number',
  bank_name: 'Bank Name',
  email: 'Email',
  state_of_residence: 'State of Residence',
  lga_of_residence: 'LGA of Residence',
  parent_guardian_name: "Parent/Guardian Name",
  parent_guardian_address: "Parent/Guardian Address",
  parent_guardian_phone: "Parent/Guardian Phone",
  education: 'Education',
  employment_status: 'Employment Status',
  completion_status: 'Completion Status',
  specialization: 'Specialization',
  status: 'Status',
  batch_id: 'Batch',
};

const allFields = Object.keys(fieldLabels);

const ProfileLinks = () => {
  const linksBaseUrl = usePublicLinksBaseUrl();
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({ description: '', editable_fields: [] });
  const [operationProgress, setOperationProgress] = useState({ type: null, progress: 0, message: '' });
  const [generating, setGenerating] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, title: '', message: '', onConfirm: null });

  useEffect(() => {
    fetchLinks();
  }, []);

  const fetchLinks = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/profile-links');
      setLinks(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching profile links:', error);
      showNotification('Error fetching profile links', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateLink = async (e) => {
    e.preventDefault();
    setGenerating(true);
    setOperationProgress({ type: 'generate', progress: 20, message: 'Generating link...' });
    try {
      const response = await axios.post('/api/profile-links/generate', formData);
      setOperationProgress({ type: 'generate', progress: 100, message: 'Link generated!' });
      const fullUrl = `${linksBaseUrl}/profile-update/${response.data.token}`;
      navigator.clipboard.writeText(fullUrl);
      showNotification('Link generated & copied to clipboard!', 'success');
      setFormData({ description: '', editable_fields: [] });
      setShowModal(false);
      fetchLinks();
    } catch (error) {
      showNotification(error.response?.data?.error || 'Error generating link', 'error');
    } finally {
      setGenerating(false);
      setTimeout(() => setOperationProgress({ type: null, progress: 0, message: '' }), 800);
    }
  };

  const toggleField = (field) => {
    setFormData(prev => {
      const currentFields = prev.editable_fields || [];
      if (currentFields.includes(field)) {
        return { ...prev, editable_fields: currentFields.filter(f => f !== field) };
      } else {
        return { ...prev, editable_fields: [...currentFields, field] };
      }
    });
  };

  const selectAllFields = () => {
    setFormData(prev => ({ ...prev, editable_fields: [...allFields] }));
  };

  const deselectAllFields = () => {
    setFormData(prev => ({ ...prev, editable_fields: [] }));
  };

  const copyLink = (token) => {
    const fullUrl = `${linksBaseUrl}/profile-update/${token}`;
    navigator.clipboard.writeText(fullUrl);
    showNotification('Link copied to clipboard!', 'success');
  };

  const deactivateLink = async (id) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Deactivate Link',
      message: 'Are you sure you want to deactivate this profile update link?',
      onConfirm: async () => {
        try {
          await axios.put(`/api/profile-links/${id}/deactivate`);
          showNotification('Link deactivated successfully', 'success');
          fetchLinks();
          setConfirmDialog({ isOpen: false, title: '', message: '', onConfirm: null });
        } catch (error) {
          showNotification('Error deactivating link', 'error');
          setConfirmDialog({ isOpen: false, title: '', message: '', onConfirm: null });
        }
      }
    });
  };

  const deleteLink = async (id) => {
    const link = Array.isArray(links) ? links.find(l => l.id === id) : null;
    setConfirmDialog({
      isOpen: true,
      title: 'Delete Profile Link',
      message: `Are you sure you want to delete the profile link "${link?.description || 'Profile Update Link'}"? This action cannot be undone.`,
      onConfirm: async () => {
        setOperationProgress({ type: 'delete', progress: 0, message: 'Deleting link...' });
        try {
          setOperationProgress(prev => ({ ...prev, progress: 50 }));
          await axios.delete(`/api/profile-links/${id}`);
          setOperationProgress(prev => ({ ...prev, progress: 100, message: 'Link deleted!' }));
          setTimeout(() => {
            setOperationProgress({ type: null, progress: 0, message: '' });
            showNotification('Profile link deleted successfully', 'success');
            fetchLinks();
            setConfirmDialog({ isOpen: false, title: '', message: '', onConfirm: null });
          }, 500);
        } catch (error) {
          setOperationProgress({ type: null, progress: 0, message: '' });
          showNotification(error.response?.data?.error || 'Error deleting link', 'error');
          setConfirmDialog({ isOpen: false, title: '', message: '', onConfirm: null });
        }
      }
    });
  };

  const deleteAllLinks = async () => {
    setConfirmDialog({
      isOpen: true,
      title: 'Delete All Profile Links',
      message: 'Are you sure you want to delete ALL profile update links? This action cannot be undone!',
      onConfirm: async () => {
        setOperationProgress({ type: 'delete-all', progress: 0, message: 'Deleting all links...' });
        try {
          setOperationProgress(prev => ({ ...prev, progress: 50 }));
          const response = await axios.delete('/api/profile-links/all');
          setOperationProgress(prev => ({ ...prev, progress: 100, message: 'All links deleted!' }));
          setTimeout(() => {
            setOperationProgress({ type: null, progress: 0, message: '' });
            showNotification(response.data.message || 'All profile links deleted successfully', 'success');
            fetchLinks();
            setConfirmDialog({ isOpen: false, title: '', message: '', onConfirm: null });
          }, 500);
        } catch (error) {
          setOperationProgress({ type: null, progress: 0, message: '' });
          showNotification(error.response?.data?.error || 'Error deleting all links', 'error');
          setConfirmDialog({ isOpen: false, title: '', message: '', onConfirm: null });
        }
      }
    });
  };

  return (
    <div className="profile-links">
      {operationProgress.type && (
        <div className="operation-progress-overlay">
          <div className="operation-progress-card">
            <ProgressBar
              progress={operationProgress.progress}
              message={operationProgress.message}
            />
          </div>
        </div>
      )}

      <div className="profile-links-header">
        <div>
          <h1>Profile Update Links</h1>
          <p>Generate secure links so trainees can review and update their records themselves.</p>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          {links.length > 0 && (
            <button 
              className="btn btn-danger" 
              onClick={deleteAllLinks}
              disabled={operationProgress.type === 'delete-all'}
            >
              <FiTrash2 /> Delete All Links
            </button>
          )}
          <button className="btn btn-primary" onClick={() => setShowModal(true)}>
            <FiLink /> Generate Link
          </button>
        </div>
      </div>

      {loading ? (
        <div className="loading">Loading links...</div>
      ) : !Array.isArray(links) || links.length === 0 ? (
        <div className="empty-state">
          <p>No profile update links generated yet.</p>
        </div>
      ) : (
        <div className="profile-links-list">
          {Array.isArray(links) && links.map((link) => (
            <div key={link.id} className="profile-link-card">
              <div className="link-card-header">
                <div>
                  <h3>{link.description || 'Profile Update Link'}</h3>
                  <p className="link-date">{new Date(link.created_at).toLocaleString()}</p>
                </div>
                <span className={`status-badge ${link.is_active ? 'active' : 'inactive'}`}>
                  {link.is_active ? 'Active' : 'Inactive'}
                </span>
              </div>
              <div className="link-card-body">
                <small>{linksBaseUrl}/profile-update/{link.token}</small>
              </div>
              <div className="link-card-actions">
                <button className="btn btn-secondary" onClick={() => copyLink(link.token)}>
                  <FiCopy /> Copy
                </button>
                <button 
                  className="btn btn-danger" 
                  onClick={() => deleteLink(link.id)}
                  disabled={operationProgress.type === 'delete'}
                >
                  <FiTrash2 /> Delete
                </button>
                {link.is_active && (
                  <button className="btn btn-warning" onClick={() => deactivateLink(link.id)}>
                    Deactivate
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <div className="modal" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Create Profile Update Link</h2>
              <button className="close-btn" onClick={() => setShowModal(false)}>
                <FiX />
              </button>
            </div>
            <form onSubmit={handleGenerateLink} className="profile-link-form">
              <div className="form-group">
                <label>Link description (optional)</label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="e.g., Batch A Self Update"
                  disabled={generating}
                />
              </div>
              
              <div className="form-group">
                <label>Select Editable Fields</label>
                <p className="field-selection-hint">
                  Choose which fields trainees can edit. If none selected, all fields will be editable.
                </p>
                <div className="field-selection-actions">
                  <button
                    type="button"
                    className="btn btn-sm btn-secondary"
                    onClick={selectAllFields}
                    disabled={generating}
                  >
                    Select All
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-secondary"
                    onClick={deselectAllFields}
                    disabled={generating}
                  >
                    Deselect All
                  </button>
                </div>
                <div className="field-selection-grid">
                  {allFields.map(field => (
                    <label key={field} className="field-checkbox">
                      <input
                        type="checkbox"
                        checked={(formData.editable_fields || []).includes(field)}
                        onChange={() => toggleField(field)}
                        disabled={generating}
                      />
                      <span>{fieldLabels[field]}</span>
                    </label>
                  ))}
                </div>
              </div>
              
              <div className="modal-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowModal(false);
                    setFormData({ description: '', editable_fields: [] });
                  }}
                  disabled={generating}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={generating}>
                  {generating ? 'Generating...' : 'Generate'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        onConfirm={confirmDialog.onConfirm || (() => {})}
        onCancel={() => setConfirmDialog({ isOpen: false, title: '', message: '', onConfirm: null })}
      />
    </div>
  );
};

export default ProfileLinks;

